import { Alert, AlertColor, Box, Button, Divider, FormControlLabel, IconButton, Snackbar, Switch, TextField, Typography } from "@mui/material";
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import PersonAddAlt1Icon from '@mui/icons-material/PersonAddAlt1';
import { Check } from "@mui/icons-material";
import { GroupsTable } from "../GroupsTable/GroupsTable";
import { useEffect, useState } from "react";
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import { useTranslation } from "react-i18next";
import { AddingUserGuest, AddUserGroup, CreateUser, EditUser, GetUser, GetUserByEmail, RemoveUserGroup, VerifyUserAddingGuest } from "../../../../../services/UserServices";
import { LoginType } from "../../../../../utils/constants/LoginTypeConstants";
import LoaderComp from "../../../../../components/Loader/LoaderComp";
import { Group } from "../../../../../model/GroupModel";
import { UserCreate, UserGroupsVO, UserOrganization } from "../../../../../model/UserModel";
import { Organization } from "../../../../../model/OrganizationModel";
import { GetOneOrganization } from "../../../../../services/OrganizationServices";
import KeydriveSnackbar from "../../../../../components/KeydriveComponents/Snackbar/KeydriveSnackbar";
import { KeyDriveDialog } from "../../../../../components/KeydriveComponents/Dialog/KeyDriveDialog";

export const GuestUserPanel = () => {
    const [email, setEmail] = useState<string | undefined>(undefined);
    const [userEditId, setUserEditId] = useState<string | undefined>(undefined);
    const [isEmailError, setIsEmailError] = useState<boolean>(false);
    const [loading, setIsLoading] = useState<boolean>(true);
    const [messageSnack, setMessageSnack] = useState<string | undefined>(undefined);
    const [colorSnack, setColorSnack] = useState<AlertColor | undefined>();
    const navigate = useNavigate();
    const { nameOrg } = useParams();
    const {t} = useTranslation();

    const handleBackClick = () => {
        navigate(`/${nameOrg}/adminConsole/usersAndGroups`);
    };

    const handleChangeEmail = (event: React.ChangeEvent<HTMLInputElement>) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        const email = event.target.value;

        setEmail(email);

        if (emailRegex.test(email)) {
            setIsEmailError(false);
        } else {
            setIsEmailError(true);
        }
    };

    const validateFields = () => {
        let isValid = true;

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!email || !emailRegex.test(email)) {
            setIsEmailError(true);
            isValid = false;
        } else {
            setIsEmailError(false);
        }

        return isValid;
    };

    const handleSave = () => {
        if (validateFields()) {
            AddingUserGuest(nameOrg!, { email: email! })
            .then(data => {
                setColorSnack("success");
                setMessageSnack(t("AdminConsole.GuestUserSnackMessage"));
            })
            .catch(err => {
                setColorSnack("error");
                setMessageSnack(err.response.data);
            })
        }
    }

    const styles = {
        container: {
            background: '#F7F8FA',
            padding: '30px',
        },
        panel: {
            background: '#FFFFFF',
            border: '1px solid #0000001A',
            borderRadius: '4px',
            height: '100%',
            padding: '30px',
            display: 'flex',
            flexDirection: 'column',
            gap: '15px',
        },
        iconButton: {
            borderRadius: '4px',
            font: 'normal normal normal 14px / 21px Segoe UI',
            color: "#22638D",
            '&:hover': {
                backgroundColor: "#F4F7F9",
            },
        },
        headerText: {
            display: 'flex',
            alignItems: 'center',
            font: 'normal normal 600 20px/ 27px Segoe UI',
        },
        saveButton: {
            height: '36px',
            backgroundColor: '#22638D',
            textTransform: 'none',
            font: 'normal normal normal 14px/21px Segoe UI',
            whiteSpace: 'nowrap',
        },
        cancelButton: {
            height: '36px',
            textTransform: 'none',
            font: 'normal normal normal 14px/21px Segoe UI',
            whiteSpace: 'nowrap',
        },
        sectionTitle: {
            font: 'normal normal 600 16px/ 24px Segoe UI',
        },
        label: {
            font: 'normal normal normal 12px / 18px Segoe UI',

        },
        textField: {
            width: '100%',
            borderBottom: '1px solid black',

            '&.MuiTextField-root': {
                border: 'none', 
            },
        },
        description: {
            font: 'normal normal normal 14px / 21px Segoe UI',
            opacity: 0.7,
        },
        switch: {
            '& .MuiSwitch-switchBase': {
                color: '#969696',
                '&.Mui-checked': {
                    color: '#22638D',
                },
            },
            '& .MuiSwitch-track': {
                backgroundColor: '#BDBDBD',
            },
        },
        twoFaText: {
            font: 'normal normal normal 14px / 21px Segoe UI',
            opacity: 0.5,
        },
    };

    return (
        <Box sx={styles.container}>
            <Box sx={styles.panel}>
                <Box>
                    <IconButton sx={styles.iconButton} onClick={handleBackClick}>
                        <ArrowBackIosIcon sx={{ height: '12px' }} />
                        <Typography>{t("AdminConsole.Back")}</Typography>
                    </IconButton>
                </Box>

                <Box display={'flex'} justifyContent={'space-between'}>
                        <>
                            <Typography sx={styles.headerText}>
                                <PersonAddAlt1Icon sx={{ width: '24px', height: '24px', marginRight: '12px', color: '#22638D' }} />
                                {t("AdminConsole.GuestUserAdd")}
                            </Typography>

                            <Button variant="contained" startIcon={<Check />} onClick={() => {
                                handleSave();
                            }} sx={styles.saveButton}>
                                {t("AdminConsole.Save")}
                            </Button>
                        </>
                </Box>

                <Divider />

                <Typography sx={styles.sectionTitle}>{t("AdminConsole.UsersInfo")}</Typography>

                <Box sx={{ display: 'flex', gap: '20px' }}>
                    <Box flex={1}>
                        <Typography sx={styles.label} color={isEmailError ? 'error' : ''}>E-mail*</Typography>
                        <TextField
                            error={isEmailError ? true : false}
                            value={email}
                            onChange={handleChangeEmail}
                            sx={styles.textField}
                            required
                            variant="standard"
                            placeholder={t("AdminConsole.TypeUserEmail")}
                            helperText={isEmailError ? t("AdminConsole.InvalidEmail") : undefined}
                            disabled={userEditId !== undefined}                                    
                            inputProps={{maxLength: 254}}
                        />
                    </Box>
                </Box>
            </Box>

            <KeydriveSnackbar 
                open={messageSnack != undefined}
                message={messageSnack}
                variant={colorSnack}
                onClose={() => setMessageSnack(undefined)}
            />
        </Box>
    );
};
